export type PhoneValidationErrorCode =
  | 'empty_after_trim'
  | 'too_long'
  | 'invalid_character'
  | 'plus_position'
  | 'missing_digit'

export type PhoneValidationError = {
  code: PhoneValidationErrorCode
  message: string
}

export type ValidatePhoneNumberOptions = {
  required?: boolean
  maxLength?: number
}

export type PhoneValidationResult = {
  isValid: boolean
  error?: PhoneValidationError
}

const DEFAULT_MAX_LENGTH = 100
const ALLOWED_PHONE_CHARACTERS = /^[0-9\s\-().#+]+$/

const errors: Record<PhoneValidationErrorCode, PhoneValidationError> = {
  empty_after_trim: {
    code: 'empty_after_trim',
    message: 'Phone number cannot be empty after trimming.',
  },
  too_long: {
    code: 'too_long',
    message: 'Phone number is too long (maximum 100 characters).',
  },
  invalid_character: {
    code: 'invalid_character',
    message: 'Phone number contains invalid characters.',
  },
  plus_position: {
    code: 'plus_position',
    message: 'Plus sign is allowed only once and must be the first character.',
  },
  missing_digit: {
    code: 'missing_digit',
    message: 'Phone number must include at least one digit.',
  },
}

export const validatePhoneNumberInput = (
  value: unknown,
  options: ValidatePhoneNumberOptions = {},
): PhoneValidationResult => {
  const {required = false, maxLength = DEFAULT_MAX_LENGTH} = options

  if (value == null || value === '') {
    return {
      isValid: !required,
      error: required ? errors.empty_after_trim : undefined,
    }
  }

  if (typeof value !== 'string') {
    return {isValid: false, error: errors.invalid_character}
  }

  const trimmedValue = value.trim()

  if (!trimmedValue) {
    return {isValid: false, error: errors.empty_after_trim}
  }

  if (trimmedValue.length > maxLength) {
    return {isValid: false, error: errors.too_long}
  }

  if (!ALLOWED_PHONE_CHARACTERS.test(trimmedValue)) {
    return {isValid: false, error: errors.invalid_character}
  }

  const plusMatches = trimmedValue.match(/\+/g) || []
  const hasInvalidPlus =
    plusMatches.length > 1 ||
    (plusMatches.length === 1 && trimmedValue[0] !== '+')
  if (hasInvalidPlus) {
    return {isValid: false, error: errors.plus_position}
  }

  if (!/\d/.test(trimmedValue)) {
    return {isValid: false, error: errors.missing_digit}
  }

  return {isValid: true}
}

export const translatePhoneValidationError = (
  errorCode: PhoneValidationErrorCode,
  t: (key: string) => string,
) => {
  switch (errorCode) {
    case 'empty_after_trim':
      return t('Phone number cannot be empty after trimming.')
    case 'too_long':
      return t('Phone number is too long (maximum 100 characters).')
    case 'invalid_character':
      return t('Phone number contains invalid characters.')
    case 'plus_position':
      return t(
        'Plus sign is allowed only once and must be the first character.',
      )
    case 'missing_digit':
      return t('Phone number must include at least one digit.')
    default:
      return t('Invalid phone number.')
  }
}
